// common/services/account/sportsPaySignUp.ts
import req from '../../api/request'
import type {
  SportsPayOnboarding,
  SportsPayOnboardingCreated,
  SportsPaySignupPayload,
} from '../../features/account/sportsPaySignUp/types'

/** Both calls live on the same org-scoped resource; only the verb differs. */
export const sportsPayOnboardingEndpoint = (organizationId: string): string =>
  `/organizations/${organizationId}/payment-provider/sportspay/onboarding`

/**
 * The current SportsPay onboarding application for an organization, or `null`
 * when there is none.
 *
 * NEVER REJECTS. This call gates the whole signup modal, so a failure has to
 * degrade to "no application yet" and let the form render — otherwise a backend
 * blip would lock the user out of applying entirely. Re-applying after a failed
 * read is safe: the POST answers 200 with `created: false` when an application
 * already exists, so nothing is duplicated.
 */
export const getSportsPayOnboarding = async (
  organizationId: string
): Promise<SportsPayOnboarding | null> => {
  try {
    const response = await req(sportsPayOnboardingEndpoint(organizationId))
    // The endpoint has been seen returning the application at the top level and
    // wrapped in `data`; accept either rather than depending on the envelope.
    const application = (response?.data ?? response) as
      | SportsPayOnboarding
      | undefined

    // `app_id` is what makes a response an application — a 200 carrying an
    // empty object means "none yet", not an application to display.
    return application?.app_id ? application : null
  } catch {
    return null
  }
}

/**
 * Files a SportsPay onboarding application for an organization.
 *
 * Rejects with the raw API error object on failure so callers can read
 * `invalid_fields` and surface them (see `applySportsPayApiError`). The 422 from
 * this endpoint carries an ARRAY of account-level field names plus a top-level
 * `message`, which surfaces as a toast rather than inline field errors.
 *
 * Every key in `payload` is optional: the server derives anything absent from
 * the organization's account.
 */
export const submitSportsPaySignup = async (
  organizationId: string,
  payload: SportsPaySignupPayload
): Promise<SportsPayOnboardingCreated> => {
  const response = await req(sportsPayOnboardingEndpoint(organizationId), {
    method: 'POST',
    body: JSON.stringify(payload),
  })
  return (response?.data ?? response) as SportsPayOnboardingCreated
}

/** Endpoint returning the hosted SportsPay registration URL for an org. */
export const sportsPayUrlEndpoint = (organizationId: string): string =>
  `/organizations/${organizationId}/payment-provider/sportspay/url`

/**
 * The hosted SportsPay registration URL for an organization.
 *
 * The server builds the whole query string — callback, org id, contact and
 * address prefill — so the client just opens what it is handed. Nothing here
 * inspects or rewrites it beyond the caller's `resolveSportsPayAppLink` safety
 * check.
 *
 * Unlike `getSportsPayOnboarding`, this DOES reject: it fires on an explicit
 * user action with nothing to fall back to, so the caller surfaces the failure
 * rather than silently opening an empty frame.
 */
export const getSportsPayUrl = async (
  organizationId: string
): Promise<string | undefined> => {
  const response = await req(sportsPayUrlEndpoint(organizationId))
  const payload = (response?.data ?? response) as { url?: string } | undefined
  return payload?.url
}

/** SportsPay's id in the payment-provider table. Stripe is '1'. */
export const SPORTS_PAY_PROVIDER_TYPE_ID = '2'

export interface NotifySportsPayCompleteArgs {
  /** From the org payload's `account_id` — this endpoint is ACCOUNT-scoped. */
  accountId: string
  /** SportsPay's `code` param: the terminal id. */
  terminalId: string
  /** SportsPay's `status` param, already underscore-normalized. */
  status: string
}

/**
 * Records a completed SportsPay registration against the account.
 *
 * The same endpoint and body the account-settings flow uses (`putAccountInfo`
 * in `common/hooks/usePaymentProviderConnection.ts`). We call it from the
 * org-level flow because SportsPay's callback lands INSIDE our iframe rather
 * than hitting the server — so without this the backend would never learn the
 * registration finished.
 *
 * Note the scope difference: this is ACCOUNT-scoped (`account_id` comes from
 * the org payload), while the rest of this feature is org-scoped.
 */
export const notifySportsPayComplete = async ({
  accountId,
  terminalId,
  status,
}: NotifySportsPayCompleteArgs): Promise<unknown> =>
  req(`/account/${accountId}/payment_provider/onboarding`, {
    method: 'PUT',
    body: JSON.stringify({
      sportspay_terminal_id: terminalId,
      sportspay_account_status: status,
      payment_provider_type_id: SPORTS_PAY_PROVIDER_TYPE_ID,
    }),
  })
